/**
 * Cifrado simetrico de los blobs en IndexedDB (ADR-002 + ADR-007).
 *
 * Flujo:
 *   1. Al login, derivamos una AES-GCM 256-bit key con PBKDF2 a partir
 *      del password + un salt deterministico que combina tenantId y un
 *      "salt purpose" fijo. La key queda solo en memoria del runtime
 *      JS (no en sessionStorage / IndexedDB). Si el usuario cierra la
 *      pestaña la perdemos y debe re-loguear online para regenerarla.
 *   2. Cada write a IDB que contenga datos sensibles se cifra antes.
 *   3. Cada read los descifra.
 *
 * Tradeoff aceptado: el salt no es secreto, pero el password si lo es
 * (passwords con entropia >= 12 chars + bcrypt server-side). Sprint 8
 * (hardening pre go-live) puede subir iteraciones PBKDF2 si benchmark
 * lo justifica.
 */
const ITERATIONS = 100_000;
const KEY_LENGTH_BITS = 256;
const IV_LENGTH_BYTES = 12;
const SALT_PURPOSE = 'assessos-field/idb/v1';

function getSubtle(): SubtleCrypto {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('Web Crypto API no disponible');
  }
  return crypto.subtle;
}

function strToBytes(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

function bytesToStr(b: Uint8Array): string {
  return new TextDecoder().decode(b);
}

// TS 5.7+ tipea Uint8Array como Uint8Array<ArrayBufferLike>, pero las
// Web Crypto APIs piden BufferSource = ArrayBufferView<ArrayBuffer>.
// Castear via unknown evita re-copiar el buffer en cada llamada.
function asBufferSource(b: Uint8Array): BufferSource {
  return b as unknown as BufferSource;
}

export async function deriveKey(password: string, tenantId: string): Promise<CryptoKey> {
  const subtle = getSubtle();
  const baseKey = await subtle.importKey(
    'raw',
    asBufferSource(strToBytes(password)),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  const salt = strToBytes(`${SALT_PURPOSE}:${tenantId}`);
  return subtle.deriveKey(
    { name: 'PBKDF2', salt: asBufferSource(salt), iterations: ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: KEY_LENGTH_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
}

export interface EncryptedBlob {
  iv: Uint8Array;
  ciphertext: Uint8Array;
}

export async function encryptJson(key: CryptoKey, value: unknown): Promise<EncryptedBlob> {
  const subtle = getSubtle();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH_BYTES));
  const plaintext = strToBytes(JSON.stringify(value));
  const ciphertext = new Uint8Array(
    await subtle.encrypt(
      { name: 'AES-GCM', iv: asBufferSource(iv) },
      key,
      asBufferSource(plaintext),
    ),
  );
  return { iv, ciphertext };
}

export async function decryptJson<T = unknown>(key: CryptoKey, blob: EncryptedBlob): Promise<T> {
  const subtle = getSubtle();
  const plaintext = new Uint8Array(
    await subtle.decrypt(
      { name: 'AES-GCM', iv: asBufferSource(blob.iv) },
      key,
      asBufferSource(blob.ciphertext),
    ),
  );
  return JSON.parse(bytesToStr(plaintext)) as T;
}

export async function encryptBytes(key: CryptoKey, bytes: Uint8Array): Promise<EncryptedBlob> {
  const subtle = getSubtle();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH_BYTES));
  const ciphertext = new Uint8Array(
    await subtle.encrypt({ name: 'AES-GCM', iv: asBufferSource(iv) }, key, asBufferSource(bytes)),
  );
  return { iv, ciphertext };
}

export async function decryptBytes(key: CryptoKey, blob: EncryptedBlob): Promise<Uint8Array> {
  const subtle = getSubtle();
  const plain = new Uint8Array(
    await subtle.decrypt(
      { name: 'AES-GCM', iv: asBufferSource(blob.iv) },
      key,
      asBufferSource(blob.ciphertext),
    ),
  );
  return plain;
}
